// Compare a DuckDB query result against an expected CSV. Logic is a
// direct port of `tests/refdiff_runner.zig`'s parseCsv + compareCsv +
// cellEqual: case-insensitive column-name compare, numeric cells with
// 1e-9 relative tolerance, trimmed text. The Zig harness is the
// canonical reference — keep this file in sync if the rules change
// there.

import type { QueryResult } from '../duckdb';

const FLOAT_TOLERANCE = 1e-9;

export interface DiffLine {
  message: string;
}

export type CheckResult =
  | { ok: true }
  | { ok: false; diffs: DiffLine[] };

interface Csv {
  columns: string[];
  rows: string[][];
}

export function parseExpectedCsv(text: string): Csv {
  const lines: string[] = [];
  for (const raw of text.split('\n')) {
    let line = raw;
    if (line.endsWith('\r')) line = line.slice(0, -1);
    if (line.length === 0) continue;
    lines.push(line);
  }
  if (lines.length === 0) return { columns: [], rows: [] };
  const columns = splitCsv(lines[0]);
  const rows = lines.slice(1).map(splitCsv);
  return { columns, rows };
}

/// Project a `QueryResult` (Arrow-derived `unknown[][]`) into the same
/// trimmed-string shape as the expected-CSV side so cellEqual can
/// compare them apples-to-apples.
export function queryResultAsCsv(qr: QueryResult): Csv {
  const rows = qr.rows.map((row) =>
    row.map((cell) => (cell == null ? '' : String(cell))),
  );
  return { columns: qr.columns.slice(), rows };
}

export function compareCsv(expected: Csv, actual: Csv): CheckResult {
  const diffs: DiffLine[] = [];

  if (expected.columns.length !== actual.columns.length) {
    diffs.push({
      message: `column count mismatch: expected ${expected.columns.length}, actual ${actual.columns.length}`,
    });
  } else {
    for (let i = 0; i < expected.columns.length; i++) {
      const ec = expected.columns[i];
      const ac = actual.columns[i];
      if (!eqlIgnoreCase(ec, ac)) {
        diffs.push({ message: `column ${i} name: expected '${ec}', actual '${ac}'` });
      }
    }
  }

  if (expected.rows.length !== actual.rows.length) {
    diffs.push({
      message: `row count: expected ${expected.rows.length}, actual ${actual.rows.length}`,
    });
  }

  const minRows = Math.min(expected.rows.length, actual.rows.length);
  for (let r = 0; r < minRows; r++) {
    const eRow = expected.rows[r];
    const aRow = actual.rows[r];
    const minCols = Math.min(eRow.length, aRow.length);
    for (let c = 0; c < minCols; c++) {
      if (!cellEqual(eRow[c], aRow[c])) {
        const colName = c < expected.columns.length ? expected.columns[c] : '?';
        diffs.push({
          message: `row ${r + 1} col '${colName}': expected '${eRow[c]}', actual '${aRow[c]}'`,
        });
      }
    }
    if (eRow.length !== aRow.length) {
      diffs.push({
        message: `row ${r + 1} field count: expected ${eRow.length}, actual ${aRow.length}`,
      });
    }
  }

  if (diffs.length === 0) return { ok: true };
  return { ok: false, diffs };
}

function splitCsv(line: string): string[] {
  return line.split(',').map((s) => s.replace(/^[ \t]+|[ \t]+$/g, ''));
}

function eqlIgnoreCase(a: string, b: string): boolean {
  return a.length === b.length && a.toLowerCase() === b.toLowerCase();
}

/// Cell equality with numeric tolerance and trimmed text. Mirrors
/// `cellEqual` in tests/refdiff_runner.zig: try byte-equal (case-
/// insensitive) first; fall back to a parseFloat compare with a 1e-9
/// relative tolerance.
function cellEqual(a: string, b: string): boolean {
  if (eqlIgnoreCase(a, b)) return true;
  const af = parseFloat(a);
  const bf = parseFloat(b);
  if (Number.isNaN(af) || Number.isNaN(bf)) return false;
  if (af === bf) return true;
  const diff = Math.abs(af - bf);
  const scale = Math.max(Math.abs(af), Math.abs(bf));
  return diff <= FLOAT_TOLERANCE * Math.max(1, scale);
}
